import { Box, Typography } from "@material-ui/core";
import { styled } from "@material-ui/core/styles";
import InfoIcon from "@material-ui/icons/Info";
import PropTypes from "prop-types";
import * as React from "react";
import { useLocation } from "react-router-dom";
import { Money } from "../../../domain";
import { useCart } from "../../../hooks";
import { ErrorUI, InfoTooltip, LoadingUI } from "../../../theme";
import * as utils from "../../Checkout/utils";
import PromoCode from "./PromoCode";
import PromoCodeInput from "./PromoCodeInput";
import ImportFeeNotices from "./ImportFeeNotices";
import ShippingText from "./ShippingText";

const StyledTotals = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "auto 8rem",
  alignItems: "center",
  gap: theme.spacing(0, 1),
  "& *:nth-child(even)": {
    textAlign: "right",
  },
}));

const StyledTotal = styled("p")(({ theme }) => ({
  ...theme.typography.h6,
  margin: 0,
  fontWeight: theme.typography.fontWeightRegular,
}));

const StyledFeedback = styled("div")(({ theme }) => ({
  ...theme.typography.caption,
  backgroundColor: theme.palette.green.extraLight,
  borderRadius: theme.shape.borderRadius,
  display: "grid",
  gridTemplateColumns: "auto 1fr",
  alignItems: "center",
  gap: theme.spacing(1),
  fontWeight: theme.typography.fontWeightMedium,
  margin: 0,
  padding: theme.spacing(0.5, 1),
  color: theme.palette.green.extraDark,
  "& svg": {
    color: theme.palette.green.dark,
    fontSize: "1.25rem",
  },
}));

const StyledContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gridGap: theme.spacing(1),
}));

const Totals = React.memo(function Totals({ Container = StyledContainer }) {
  const location = useLocation();
  const isCheckout = location.pathname.startsWith("/checkout");
  const { cart, isLoading, isError } = useCart();

  const { shipmentRequired } = React.useMemo(
    () => utils.shipmentOptionsFor(cart),
    [cart],
  );

  if (isLoading || !cart) {
    return <LoadingUI />;
  }

  if (isError) {
    return <ErrorUI message="Unable to load cart" />;
  }

  const { totals, promoCode } = cart;

  return (
    <Container>
      {cart.shippingFeedback && !isCheckout ? (
        <StyledFeedback>
          <InfoIcon />
          <span>{cart.shippingFeedback}</span>
        </StyledFeedback>
      ) : null}

      <StyledTotals>
        <Typography variant="body2" color="textSecondary">
          Subtotal
        </Typography>

        <Typography variant="body2" color="textSecondary">
          {Money.add(totals.subtotal, totals.productBump).formattedPrice}
        </Typography>

        {totals.serviceFee.value > 0 && (
          <>
            <Box display="flex" alignItems="center">
              <Typography variant="body2" color="textSecondary">
                Service Fee
              </Typography>

              <InfoTooltip
                leaveTouchDelay={6000}
                size="small"
                title="About Our Fees"
                content="Our service fee helps us cover the costs of running the platform, including organizing the signing events and shipping your items to the Talent for signing."
              />
            </Box>

            <Typography variant="body2" color="textSecondary">
              {totals.serviceFee.formattedPrice}
            </Typography>
          </>
        )}

        {totals.tax.value > 0 && (
          <>
            <Typography variant="body2" color="textSecondary">
              Tax
            </Typography>

            <Typography variant="body2" color="textSecondary">
              {totals.tax.formattedPrice}
            </Typography>
          </>
        )}

        {promoCode?.appliesTo === "subtotal" && <PromoCode />}

        {shipmentRequired && (
          <>
            <Typography variant="body2" color="textSecondary">
              Shipping{" "}
              <InfoTooltip
                leaveTouchDelay={6000}
                size="small"
                title="Shipping"
                content="Shipping is calculated based on quantity and type of items. We group items by what safely fits together. Pre-Signed prints ship separately and have their own address-based shipping rate."
              />
            </Typography>

            <ShippingText
              shipping={totals.unadjustedShippingCost.value}
              adjustedShipping={totals.shippingCost.value}
            />
          </>
        )}

        {(promoCode?.appliesTo === "total" || !promoCode) && <PromoCode />}
      </StyledTotals>

      {shipmentRequired && <ImportFeeNotices importFees={totals.importFees} />}

      <StyledTotals>
        <StyledTotal>Total</StyledTotal>
        <StyledTotal>
          <strong>
            USD&nbsp;
            {totals.total.formattedPrice}
          </strong>
        </StyledTotal>
      </StyledTotals>

      <PromoCodeInput />
    </Container>
  );
});

export default Totals;

Totals.propTypes = {
  Container: PropTypes.elementType,
};
